/**
 * Pure helpers for variant overrides — key building and resolution.
 *
 * No store imports on purpose: the editor store, the action executor and the
 * serializer all use these, and importing a store from here would cycle.
 */
import type { DimensionFacets, LanguageSourceMap, VariantOverrideEntry, VariantOverrideMap } from '@/types/feed/variantOverrides';

/**
 * Tie-break between two candidate keys that scope the same number of
 * dimensions — earlier wins, so content beats layout. `lang` and `season`
 * are reserved: nothing writes them yet, but ranking them now means adding
 * those dimensions later changes no resolution code.
 */
export const DIMENSION_PRIORITY = ['lang', 'season', 'ad'];

const FACET_SEPARATOR = '|';
const VALUE_SEPARATOR = ':';

/**
 * Canonical key for a set of facets: `dim:value` pairs sorted by dimension
 * name and joined with `|`, e.g. `ad:abc|lang:fr`.
 *
 * Always build keys through here — the canonical string IS the identity, so
 * two callers describing the same context must produce the same one. Facet
 * values (uids, language codes) must never contain `|` or `:`.
 */
export function buildDimensionKey(facets: DimensionFacets): string {
  return Object.entries(facets)
    .filter(([dimension, value]) => dimension.length > 0 && value.length > 0)
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([dimension, value]) => `${dimension}${VALUE_SEPARATOR}${value}`)
    .join(FACET_SEPARATOR);
}

/** Splits a key back into facets. Malformed pairs are skipped. */
export function parseDimensionKey(key: string): DimensionFacets {
  const facets: DimensionFacets = {};
  if (key.length === 0) return facets;
  for (const part of key.split(FACET_SEPARATOR)) {
    const at = part.indexOf(VALUE_SEPARATOR);
    if (at <= 0) continue;
    facets[part.slice(0, at)] = part.slice(at + 1);
  }
  return facets;
}

/** True when `key` scopes `dimension` to `value`, compound keys included. */
export function keyHasFacet(
  key: string,
  dimension: string,
  value: string
): boolean {
  return parseDimensionKey(key)[dimension] === value;
}

/**
 * Most specific match wins: more facets beat fewer, ties go by
 * `DIMENSION_PRIORITY`. Existence decides, never truthiness — an override
 * holding an empty string is a real override.
 */
export function resolveOverrideEntry(
  map: VariantOverrideMap,
  variantId: string | undefined,
  attributeId: string,
  facets: DimensionFacets
): VariantOverrideEntry | undefined {
  return findEntry(map, variantId, attributeId, facets);
}

/**
 * Same resolution with one key left out — what this cell would show if that
 * entry did not exist. Compare against the entry's `baseValue` to tell
 * whether it has gone stale.
 */
export function resolveWithoutKey(
  map: VariantOverrideMap,
  variantId: string | undefined,
  attributeId: string,
  facets: DimensionFacets,
  excludeKey: string
): VariantOverrideEntry | undefined {
  return findEntry(map, variantId, attributeId, facets, excludeKey);
}

/**
 * Drops entries whose row no longer exists. Called at serialize time only —
 * the live map keeps orphans so undoing a row delete brings its overrides
 * back with it.
 */
export function pruneVariantOverridesToRows(
  map: VariantOverrideMap,
  liveIds: string[]
): VariantOverrideMap {
  return pruneToRows(map, liveIds);
}

/** Same policy for the per-row source languages. */
export function pruneLanguageSourcesToRows(
  map: LanguageSourceMap,
  liveIds: string[]
): LanguageSourceMap {
  return pruneToRows(map, liveIds);
}

function pruneToRows<V>(
  map: Record<string, V>,
  liveIds: string[]
): Record<string, V> {
  const live = new Set(liveIds);
  const pruned: Record<string, V> = {};
  for (const [variantId, value] of Object.entries(map)) {
    if (!live.has(variantId)) continue;
    pruned[variantId] = value;
  }
  return pruned;
}

/**
 * Helpers
 */

function findEntry(
  map: VariantOverrideMap,
  variantId: string | undefined,
  attributeId: string,
  facets: DimensionFacets,
  excludeKey?: string
): VariantOverrideEntry | undefined {
  if (!variantId) return undefined;
  const byKey = map[variantId]?.[attributeId];
  if (!byKey) return undefined;

  for (const key of candidateKeys(facets)) {
    if (key === excludeKey) continue;
    const entry = byKey[key];
    if (entry !== undefined) return entry;
  }
  return undefined;
}

/**
 * Every key the active context could match, most specific first. Dimension
 * count is tiny, so walking all 2^n subsets beats maintaining an index.
 */
function candidateKeys(facets: DimensionFacets): string[] {
  const dimensions = Object.keys(facets).filter(
    dimension => (facets[dimension] ?? '').length > 0
  );

  const subsets: string[][] = [];
  for (let mask = 1; mask < 1 << dimensions.length; mask++) {
    const subset: string[] = [];
    for (const [i, dimension] of dimensions.entries()) {
      if (mask & (1 << i)) subset.push(dimension as string);
    }
    subsets.push(subset);
  }

  return subsets.toSorted(compareSubsets).map(subset => {
    const picked: DimensionFacets = {};
    for (const dimension of subset) picked[dimension] = facets[dimension] ?? '';
    return buildDimensionKey(picked);
  });
}

/** More facets first; equal counts fall back to `DIMENSION_PRIORITY`. */
function compareSubsets(a: string[], b: string[]): number {
  if (a.length !== b.length) return b.length - a.length;
  const aRanks = a.map(priorityOf).toSorted((x, y) => x - y);
  const bRanks = b.map(priorityOf).toSorted((x, y) => x - y);
  for (const [i, aRank] of aRanks.entries()) {
    const diff = (aRank ?? 0) - (bRanks[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function priorityOf(dimension: string): number {
  const index = DIMENSION_PRIORITY.indexOf(dimension);
  return index === -1 ? DIMENSION_PRIORITY.length : index;
}
